/**
 * Site Health Scores — ranked list of all sites with combined health score.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@/services/apiClient';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

interface SiteScore {
  siteNumber: string;
  branchName: string;
  score: number;
  color: 'green' | 'amber' | 'red';
  breakdown: { ageScore: number; ratioScore: number; offlineScore: number; bandwidthScore: number; ticketScore: number; circuitScore: number };
  metrics: {
    devices: number; employees: number; avgDeviceAge: number | null;
    deviceRatio: number | null; offlineCount: number; offlinePct: number;
    circuits: number; avgBandwidthMbps: number | null; bandwidthCapacity: number | null;
    bandwidthUtilPct: number | null; ticketCount: number; ticketsPerEmployee: number;
  };
}

interface HealthData {
  summary: { totalSites: number; avgScore: number; redCount: number; amberCount: number; greenCount: number };
  sites: SiteScore[];
  scoring: { description: string; maxScore: number; factors: Array<{ name: string; maxPoints: number; description: string }>; colors: Record<string, string> };
  generatedAt: string;
}

type SortKey = 'score' | 'age' | 'ratio' | 'offline' | 'bandwidth' | 'tickets' | 'name';

export function SiteHealthScores() {
  const [sortBy, setSortBy] = useState<SortKey>('score');
  const [filterColor, setFilterColor] = useState<'all' | 'red' | 'amber' | 'green'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['site-health-scores'],
    queryFn: () => apiClient.get<HealthData>('site-health-scores'),
  });

  if (isLoading) return <LoadingSpinner message="Calculating site health scores..." />;
  if (error) return <ErrorMessage message={(error as Error).message} />;
  if (!data) return null;

  const { summary, sites, scoring } = data;

  // Sort
  const sorted = [...sites].sort((a, b) => {
    switch (sortBy) {
      case 'score': return b.score - a.score;
      case 'age': return (b.metrics.avgDeviceAge ?? 0) - (a.metrics.avgDeviceAge ?? 0);
      case 'ratio': return (b.metrics.deviceRatio ?? 0) - (a.metrics.deviceRatio ?? 0);
      case 'offline': return b.metrics.offlinePct - a.metrics.offlinePct;
      case 'bandwidth': return (b.metrics.bandwidthUtilPct ?? 0) - (a.metrics.bandwidthUtilPct ?? 0);
      case 'tickets': return b.metrics.ticketsPerEmployee - a.metrics.ticketsPerEmployee;
      case 'name': return a.branchName.localeCompare(b.branchName);
      default: return 0;
    }
  });

  // Filter
  const filtered = filterColor === 'all' ? sorted : sorted.filter(s => s.color === filterColor);

  const colorDot = (color: string) => color === 'red' ? 'bg-red-500' : color === 'amber' ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div>
      <h1 className="text-lg font-semibold text-text-primary mb-1">Site Health Scores</h1>
      <p className="text-sm text-slate-500 mb-6">{scoring.description}</p>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-border p-4 text-center">
          <div className="text-2xl font-bold text-text-primary">{summary.totalSites}</div>
          <div className="text-xs text-slate-500">Total Sites</div>
        </div>
        <div className="bg-white rounded-lg border border-border p-4 text-center">
          <div className="text-2xl font-bold text-text-primary">{summary.avgScore}</div>
          <div className="text-xs text-slate-500">Avg Score</div>
        </div>
        <div className="bg-white rounded-lg border border-border p-4 text-center cursor-pointer hover:border-red-300" onClick={() => setFilterColor(filterColor === 'red' ? 'all' : 'red')}>
          <div className="text-2xl font-bold text-red-600">{summary.redCount}</div>
          <div className="text-xs text-slate-500">Critical (50+)</div>
        </div>
        <div className="bg-white rounded-lg border border-border p-4 text-center cursor-pointer hover:border-amber-300" onClick={() => setFilterColor(filterColor === 'amber' ? 'all' : 'amber')}>
          <div className="text-2xl font-bold text-amber-600">{summary.amberCount}</div>
          <div className="text-xs text-slate-500">Warning (30-49)</div>
        </div>
        <div className="bg-white rounded-lg border border-border p-4 text-center cursor-pointer hover:border-emerald-300" onClick={() => setFilterColor(filterColor === 'green' ? 'all' : 'green')}>
          <div className="text-2xl font-bold text-emerald-600">{summary.greenCount}</div>
          <div className="text-xs text-slate-500">Healthy (&lt;30)</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Filter:</span>
          {(['all', 'red', 'amber', 'green'] as const).map(c => (
            <button key={c} onClick={() => setFilterColor(c)} className={`px-2 py-1 rounded text-xs font-medium ${filterColor === c ? 'bg-baker-red text-white' : 'bg-white border border-border text-slate-600 hover:bg-slate-50'}`}>
              {c === 'all' ? 'All' : c.charAt(0).toUpperCase() + c.slice(1)} {c !== 'all' && `(${c === 'red' ? summary.redCount : c === 'amber' ? summary.amberCount : summary.greenCount})`}
            </button>
          ))}
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value as SortKey)} className="py-1.5 px-2 rounded border border-border text-xs bg-white">
          <option value="score">Sort: Score (worst first)</option>
          <option value="age">Sort: Device Age</option>
          <option value="offline">Sort: Offline %</option>
          <option value="bandwidth">Sort: Bandwidth</option>
          <option value="tickets">Sort: Tickets/Employee</option>
          <option value="ratio">Sort: Device Ratio</option>
          <option value="name">Sort: Name</option>
        </select>
      </div>

      {/* Site Table */}
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white z-10"><tr className="border-b-2 border-border">
              <th className="text-left py-2 px-3 font-medium text-slate-600 w-8"></th>
              <th className="text-left py-2 px-3 font-medium text-slate-600">Site</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">Score</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">Devices</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">Employees</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">Avg Age</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">Offline</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">Circuits</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">BW Util</th>
              <th className="text-right py-2 px-3 font-medium text-slate-600">Tickets/Emp</th>
            </tr></thead>
            <tbody>
              {filtered.map(site => (
                <>
                  <tr key={site.siteNumber} className="border-b border-border-subtle hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(expanded === site.siteNumber ? null : site.siteNumber)}>
                    <td className="py-2 px-3"><div className={`w-3 h-3 rounded-full ${colorDot(site.color)}`} /></td>
                    <td className="py-2 px-3">
                      <Link to={`/sites/${site.siteNumber}`} className="text-baker-red no-underline hover:underline font-medium" onClick={e => e.stopPropagation()}>
                        {site.siteNumber} - {site.branchName}
                      </Link>
                    </td>
                    <td className={`py-2 px-3 text-right font-bold ${site.color === 'red' ? 'text-red-600' : site.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`}>{site.score}</td>
                    <td className="py-2 px-3 text-right">{site.metrics.devices}</td>
                    <td className="py-2 px-3 text-right">{site.metrics.employees}</td>
                    <td className={`py-2 px-3 text-right ${site.metrics.avgDeviceAge && site.metrics.avgDeviceAge > 4 ? 'text-red-600 font-medium' : ''}`}>{site.metrics.avgDeviceAge ? `${site.metrics.avgDeviceAge}yr` : '—'}</td>
                    <td className={`py-2 px-3 text-right ${site.metrics.offlinePct > 20 ? 'text-red-600 font-medium' : ''}`}>{site.metrics.offlineCount > 0 ? `${site.metrics.offlineCount} (${site.metrics.offlinePct}%)` : '0'}</td>
                    <td className={`py-2 px-3 text-right ${site.metrics.circuits < 2 ? 'text-amber-600' : ''}`}>{site.metrics.circuits}</td>
                    <td className={`py-2 px-3 text-right ${site.metrics.bandwidthUtilPct && site.metrics.bandwidthUtilPct > 80 ? 'text-red-600 font-medium' : ''}`}>{site.metrics.bandwidthUtilPct != null ? `${site.metrics.bandwidthUtilPct}%` : '—'}</td>
                    <td className={`py-2 px-3 text-right ${site.metrics.ticketsPerEmployee > 4 ? 'text-red-600 font-medium' : ''}`}>{site.metrics.ticketsPerEmployee}</td>
                  </tr>
                  {expanded === site.siteNumber && (
                    <tr key={`${site.siteNumber}-detail`} className="bg-slate-50">
                      <td colSpan={10} className="py-3 px-6">
                        <div className="grid grid-cols-6 gap-3 text-xs">
                          <ScoreBar label="Device Age" score={site.breakdown.ageScore} max={25} />
                          <ScoreBar label="Device Ratio" score={site.breakdown.ratioScore} max={20} />
                          <ScoreBar label="Offline" score={site.breakdown.offlineScore} max={20} />
                          <ScoreBar label="Bandwidth" score={site.breakdown.bandwidthScore} max={20} />
                          <ScoreBar label="Tickets" score={site.breakdown.ticketScore} max={15} />
                          <ScoreBar label="Redundancy" score={site.breakdown.circuitScore} max={10} />
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scoring Legend */}
      <div className="mt-6 bg-slate-50 rounded-lg border border-border p-5 text-xs text-slate-600">
        <h4 className="text-sm font-semibold text-text-primary mb-2">Scoring Factors</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {scoring.factors.map(f => (
            <div key={f.name}>
              <span className="font-medium">{f.name}</span> <span className="text-slate-400">(0-{f.maxPoints})</span>
              <div className="text-slate-500">{f.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScoreBar({ label, score, max }: { label: string; score: number; max: number }) {
  const pct = (score / max) * 100;
  const color = pct > 60 ? 'bg-red-400' : pct > 30 ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <div>
      <div className="flex justify-between mb-0.5">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium">{score}/{max}</span>
      </div>
      <div className="h-2 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full rounded ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
